import { TFunction } from "i18next";
import { getSubOptionsForOption } from "../../../services/api/subOption.api";
import {
  isThisArray,
  isThisArrayNotEmpty,
  ObjectFromArray,
} from "../../../services/helpers/commonHelpers";
import { OptionType, SubOptionType } from "../../../services/helpers/types";
import { Dispatch } from "@reduxjs/toolkit";

export interface ValueObjectType {
  [key: string]: string[];
}

export const getSubOptions = async (
  dispatch: Dispatch,
  options: OptionType[],
) => {
  if (options !== undefined && options.length > 0) {
    const subOptionsPromise = options.map(async (option) => {
      const subOptions = await getSubOptionsForOption(
        dispatch,
        option.optionID,
      );
      const result =
        subOptions !== undefined && subOptions.length > 0
          ? subOptions
          : undefined;

      return { ...option, subOptions: result };
    });
    const subOptions = await Promise.all(subOptionsPromise);
    return subOptions;
  }
  return [];
};

export const mapSelectedValuesHelper = (newValueObject: ValueObjectType) => {
  const result = Object.keys(newValueObject).map((key) => {
    const newValue = newValueObject[key];
    if (isThisArrayNotEmpty(newValue)) {
      const newValueSplit = newValue.map((subOption) => {
        return subOption;
      });
      return `${key} - ${newValueSplit}`;
    }
    return key;
  });
  const textValue = result.join("; ");

  return { textValue, result };
};

export const removeAttribute = <T extends object>(obj: T, key: keyof T): T => {
  const newObj = { ...obj };
  delete newObj[key];
  return newObj;
};

export const handleAddNotSelectedSubOption = (
  currentValueObject: ValueObjectType,
  subOption: SubOptionType,
  mapSelectedValues: (ValueObject: ValueObjectType) => void,
  currentOption?: OptionType,
) => {
  if (currentOption !== undefined) {
    const newValueObject = {
      ...currentValueObject,
      [currentOption.name]: [subOption.name],
    };
    mapSelectedValues(newValueObject);
  }
};

export const handleDeleteSubOptionSelection = (
  subValueArray: string[],
  currentValueObject: ValueObjectType,
  currentOption: OptionType,
  subOption: SubOptionType,
  mapSelectedValues: (ValueObject: ValueObjectType) => void,
) => {
  const filteredSubArray = subValueArray.filter(
    (value) => value !== subOption.name,
  );
  const newValueObject = {
    ...currentValueObject,
    [currentOption.name]: filteredSubArray,
  };
  if (filteredSubArray.length === 0) {
    const removedObject = removeAttribute(newValueObject, currentOption.name);
    mapSelectedValues(removedObject);
  } else {
    mapSelectedValues(newValueObject);
  }
};

export const hahndleAddSubOptionSelection = (
  currentValueObject: ValueObjectType,
  currentOption: OptionType,
  subOption: SubOptionType,
  mapSelectedValues: (ValueObject: ValueObjectType) => void,
) => {
  const newValue = [...currentValueObject[currentOption.name], subOption.name];

  const newValueObject = {
    ...currentValueObject,
    [currentOption.name]: newValue,
  };
  mapSelectedValues(newValueObject);
};

export const handleAlreadySelectedSubOption = (
  currentValueObject: ValueObjectType,
  currentOption: OptionType,
  subOption: SubOptionType,
  mapSelectedValues: (ValueObject: ValueObjectType) => void,
) => {
  const subValueArray = currentValueObject[currentOption.name];

  if (isThisArray(subValueArray)) {
    if (subValueArray.includes(subOption.name)) {
      handleDeleteSubOptionSelection(
        subValueArray,
        currentValueObject,
        currentOption,
        subOption,
        mapSelectedValues,
      );
    } else {
      hahndleAddSubOptionSelection(
        currentValueObject,
        currentOption,
        subOption,
        mapSelectedValues,
      );
    }
  }
};

export const addBaseOptionSelect = (
  option: OptionType,
  currentValueObject: ValueObjectType,
  currentOption: OptionType,
  mapSelectedValues: (
    valueObject: ValueObjectType,
    currOption?: OptionType,
  ) => void,
) => {
  const subValues = option.subOptions?.map(
    (subValue: SubOptionType) => subValue.name,
  );
  const newValueObject = {
    ...currentValueObject,
    [option.name]: subValues !== undefined ? subValues : [],
  };

  mapSelectedValues(newValueObject, option); // HERE
};

export const removeBaseOptionSelect = (
  option: OptionType,
  currentValueObject: ValueObjectType,
  mapSelectedValues: (valueObject: ValueObjectType) => void,
) => {
  const filteredValueObject = removeAttribute(currentValueObject, option.name);
  mapSelectedValues(filteredValueObject);
};

const getSubValues = (input: string) => {
  const index = input.indexOf("-");
  if (index !== -1) {
    const prefix = input.substring(0, index).trim();
    const startIndex = input[index + 1] === " " ? index + 2 : index + 1;
    const suffix = input.substring(startIndex).trim();
    return { prefix, suffix };
  }

  const trimmedInput = input.trim();
  return { prefix: trimmedInput, suffix: undefined };
};

export const getValueObjectFromValue = (values: string[]) => {
  const filteredValues = values.filter((value) => value !== "");
  if (isThisArray(filteredValues)) {
    const result = filteredValues.map((value) => {
      const { prefix, suffix } = getSubValues(value);
      if (suffix === undefined) {
        return { [prefix]: [] };
      }
      const suffixSplit = suffix.split("|");
      return { [prefix]: suffixSplit };
    });

    const finalResult = ObjectFromArray(result);

    return finalResult;
  } else {
    return undefined;
  }
};

const getTransformedNameKey = (options: OptionType[], key: string) => {
  if (isThisArrayNotEmpty(options)) {
    const currentOption = options.find((option) => option.value === key);
    if (currentOption !== undefined) {
      return currentOption.name;
    }
  }
  return key;
};

const getTransformedValueKey = (options: OptionType[], key: string) => {
  if (isThisArrayNotEmpty(options)) {
    const currentOption = options.find((option) => option.name === key);
    if (currentOption !== undefined) {
      return currentOption.value;
    }
  }
  return key;
};

export const transformValueObjectName = (
  newValueObject: ValueObjectType,
  options: OptionType[],
  currentOption?: OptionType,
) => {
  const valueObjectKeys = Object.keys(newValueObject);
  if (isThisArrayNotEmpty(valueObjectKeys)) {
    const transformed = valueObjectKeys.map((key) => {
      const objectArray = newValueObject[key];
      if (isThisArrayNotEmpty(objectArray)) {
        const arrayOfValues = objectArray.map((value) => {
          const currentSubOptions = options.find(
            (option) => option.value === key,
          )?.subOptions;

          if (
            currentSubOptions !== undefined &&
            isThisArrayNotEmpty(currentSubOptions)
          ) {
            const currentSubOption = currentSubOptions.find(
              (subOption) => subOption.value === value,
            );
            return currentSubOption?.name;
          }
          return value;
        });

        const transformedSecondLayerKey = getTransformedNameKey(options, key);
        return `${transformedSecondLayerKey} - ${arrayOfValues.join("|")}`;
      }
      const transformedFirstLayerKey = getTransformedNameKey(options, key);
      return transformedFirstLayerKey;
    });
    return transformed.join("; ");
  }
  return "";
};

export const transformValueObject = (
  newValueObject: ValueObjectType,
  options: OptionType[],
  currentOption?: OptionType,
) => {
  const valueObjectKeys = Object.keys(newValueObject);

  if (isThisArrayNotEmpty(valueObjectKeys)) {
    const transformed = valueObjectKeys.map((key) => {
      const objectArray = newValueObject[key];
      // here correct objectArray and valueObjectKeys

      if (isThisArrayNotEmpty(objectArray)) {
        const arrayOfValues = objectArray.map((value) => {
          const currentSubOptions = options.find(
            (option) => option.name === key,
          )?.subOptions;
          if (
            currentSubOptions !== undefined &&
            isThisArrayNotEmpty(currentSubOptions)
          ) {
            const currentSubOption = currentSubOptions.find(
              (subOption) => subOption.name === value,
            );
            return currentSubOption?.value;
          }
          return value;
        });
        const transformedSecondLayerKey = getTransformedValueKey(options, key);
        return `${transformedSecondLayerKey} - ${arrayOfValues.join("|")}`;
      }
      const transformedFirstLayerKey = getTransformedValueKey(options, key);
      return transformedFirstLayerKey;
    });
    return transformed.join("; ");
  }
  return "";
};

export const createTooltip = (arr: string[], t: TFunction): React.ReactNode => {
  const finalTooltip = arr.map((str) => str.replace(/\|/g, ", ")).join("\n");
  return (
    <div className="dui-select-tooltip">
      {t("currentValue")}: <br />
      {finalTooltip}
    </div>
  );
};
